import CloseIcon from '@mui/icons-material/Close';
import PersonIcon from '@mui/icons-material/Person';
import ScienceIcon from '@mui/icons-material/Science';
import StarIcon from '@mui/icons-material/Star';
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import { DateTime } from 'luxon';
import { type FC, useState } from 'react';

import ActivityDimensionChips from './ActivityDimensionChips.js';
import VisionReplayDialog, { type ReplayTarget } from './VisionReplayDialog.js';
import {
  type ActivityCapture,
  useActivityEventCaptures,
  useActivityEventVideoUrl,
} from '../../api/activity.api.js';
import { useCheckPermissions } from '../../auth/index.js';

interface Props {
  eventId: string | null;
  onClose: () => void;
}

/**
 * Every snippet the AI looked at for one check-in, not just the one it coded.
 * A monitor needs the rooms the AI did NOT attribute the activity to — that is
 * how you catch "the person it watched was the daughter, not the patient".
 */
const ActivityCaptureGallery: FC<Props> = ({ eventId, onClose }) => {
  const open = Boolean(eventId);
  const { canManageVisionDebug } = useCheckPermissions();
  const [replayTarget, setReplayTarget] = useState<ReplayTarget | null>(null);
  const { data, isLoading, isError } = useActivityEventCaptures(eventId ?? '', {
    enabled: open,
    staleTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false,
  });

  const captures = data?.items ?? [];
  // Events recorded before per-capture storage have no rows; fall back to the
  // single attributed clip so old rows stay playable.
  const showLegacyClip = !isLoading && !isError && captures.length === 0;

  return (
    <Dialog open={open} onClose={onClose} maxWidth={'lg'} fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', py: 1 }}>
        <Stack sx={{ flex: 1, minWidth: 0 }}>
          <Typography variant={'subtitle1'}>Check-in snippets</Typography>
          {captures.length > 0 && (
            <Typography variant={'caption'} color={'text.secondary'}>
              {captures.length} recording{captures.length === 1 ? '' : 's'} · all captured at the same moment
            </Typography>
          )}
        </Stack>
        <IconButton onClick={onClose} edge={'end'} size={'small'} aria-label={'Close'}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ p: 2 }}>
        {isLoading && (
          <Box sx={{ display: 'grid', placeItems: 'center', minHeight: 240 }}>
            <CircularProgress />
          </Box>
        )}

        {isError && (
          <Box sx={{ display: 'grid', placeItems: 'center', minHeight: 240 }}>
            <Typography variant={'body2'} color={'error.light'}>
              Could not load the snippets for this check-in.
            </Typography>
          </Box>
        )}

        {showLegacyClip && eventId && <LegacyClip eventId={eventId} />}

        {data?.mergedUrl && (
          <MergedComposite url={data.mergedUrl} layout={data.mergedLayout} />
        )}

        {captures.length > 0 && (
          <Box
            sx={{
              display: 'grid',
              gap: 2,
              gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(3, 1fr)' },
            }}
          >
            {captures.map((capture) => (
              <CaptureTile
                key={capture.id}
                capture={capture}
                personLabel={data?.personLabel ?? null}
                onReplay={canManageVisionDebug ? setReplayTarget : undefined}
              />
            ))}
          </Box>
        )}
      </DialogContent>
      <VisionReplayDialog target={replayTarget} onClose={() => setReplayTarget(null)} />
    </Dialog>
  );
};

interface CaptureTileProps {
  capture: ActivityCapture;
  /** The label the AI took as the patient; labels are per view, so it is only matched on the attributed capture. */
  personLabel: string | null;
  onReplay?: (target: ReplayTarget) => void;
}

const CaptureTile: FC<CaptureTileProps> = ({ capture, personLabel, onReplay }) => (
  <Stack
    spacing={1}
    sx={{
      p: 1,
      borderRadius: 1,
      border: 1,
      borderColor: capture.isAttributed ? 'primary.main' : 'divider',
      bgcolor: 'background.paper',
    }}
  >
    <Stack direction={'row'} alignItems={'center'} spacing={0.75}>
      {capture.isAttributed && (
        <Tooltip arrow title={'The activity was coded from this camera'}>
          <StarIcon fontSize={'small'} sx={{ color: 'primary.main' }} />
        </Tooltip>
      )}
      <Typography variant={'body2'} sx={{ fontWeight: 500, flex: 1, minWidth: 0 }} noWrap>
        {capture.camera.name}
      </Typography>
      {capture.lighting === 'ir' && (
        <Tooltip arrow title={'Infrared — a second look at the same viewpoint'}>
          <Chip label={'IR'} size={'small'} variant={'outlined'} sx={{ height: 18, fontSize: 10 }} />
        </Tooltip>
      )}
    </Stack>

    <Box sx={{ borderRadius: 0.5, bgcolor: 'grey.900', overflow: 'hidden', aspectRatio: '16 / 9' }}>
      {capture.url ? (
        <video
          src={capture.url}
          controls
          muted
          playsInline
          preload={'none'}
          style={{ width: '100%', height: '100%', objectFit: 'contain', background: '#000' }}
        />
      ) : (
        <Box sx={{ display: 'grid', placeItems: 'center', height: '100%' }}>
          <Typography variant={'caption'} sx={{ color: 'grey.400' }}>
            Clip not archived
          </Typography>
        </Box>
      )}
    </Box>

    <Stack spacing={0.25}>
      <Typography variant={'caption'} color={'text.disabled'}>
        {DateTime.fromISO(capture.capturedAt).toLocaleString(DateTime.TIME_WITH_SECONDS)}
      </Typography>
      {capture.sceneSummary && (
        <Typography variant={'caption'} color={'text.secondary'}>
          {capture.sceneSummary}
        </Typography>
      )}
      <CapturePeople capture={capture} patientLabel={capture.isAttributed ? personLabel : null} />
    </Stack>

    {onReplay && capture.url && (
      <Button
        size={'small'}
        startIcon={<ScienceIcon />}
        onClick={() => onReplay({
          source: { kind: 'capture', id: capture.id },
          cameraId: capture.camera.id,
          cameraName: capture.camera.name,
        })}
        sx={{ alignSelf: 'flex-start' }}
      >
        Replay through vision
      </Button>
    )}
  </Stack>
);

const CapturePeople: FC<{ capture: ActivityCapture; patientLabel: string | null }> = ({ capture, patientLabel }) => {
  if (capture.peopleCount === null) {
    return (
      <Typography variant={'caption'} color={'text.disabled'} sx={{ fontStyle: 'italic' }}>
        Not described
      </Typography>
    );
  }
  if (capture.people.length === 0) {
    return (
      <Typography variant={'caption'} color={'text.disabled'}>
        No people seen
      </Typography>
    );
  }
  return (
    <Stack spacing={0.5} sx={{ mt: 0.25 }}>
      {capture.people.map((person) => {
        const takenAsPatient = patientLabel !== null && person.label === patientLabel;
        return (
          <Stack key={person.label} spacing={0.25}>
            <Stack direction={'row'} spacing={0.5} alignItems={'flex-start'}>
              <PersonIcon sx={{ fontSize: 14, mt: 0.2, color: takenAsPatient ? 'primary.main' : 'text.disabled' }} />
              <Typography variant={'caption'} color={'text.primary'}>
                <Box component={'span'} sx={{ fontWeight: 500 }}>{person.label}</Box>
                {' — '}
                {person.description}
              </Typography>
              {takenAsPatient && (
                <Tooltip arrow title={'The AI took this person to be the patient'}>
                  <Chip
                    label={'patient'}
                    size={'small'}
                    color={'primary'}
                    sx={{ height: 18, fontSize: 10, flexShrink: 0 }}
                  />
                </Tooltip>
              )}
            </Stack>
            {person.dimensions && (
              <Box sx={{ pl: 2.25 }}>
                <ActivityDimensionChips dimensions={person.dimensions} />
              </Box>
            )}
          </Stack>
        );
      })}
    </Stack>
  );
};

/**
 * The composite the AI was actually shown under the merged strategy. Worth its
 * own slot above the per-camera tiles: when a code looks wrong, the first
 * question is whether the camera was even legible once shrunk into a tile.
 */
const MergedComposite: FC<{ url: string; layout: string | null }> = ({ url, layout }) => (
  <Stack spacing={0.75} sx={{ mb: 2 }}>
    <Stack direction={'row'} alignItems={'center'} spacing={1}>
      <Typography variant={'subtitle2'}>What the AI saw</Typography>
      {layout && (
        <Chip label={layout} size={'small'} variant={'outlined'} sx={{ height: 18, fontSize: 10 }} />
      )}
      <Typography variant={'caption'} color={'text.secondary'}>
        all cameras composited into one clip
      </Typography>
    </Stack>
    <Box sx={{ borderRadius: 0.5, bgcolor: 'grey.900', overflow: 'hidden' }}>
      <video
        src={url}
        controls
        muted
        playsInline
        preload={'none'}
        style={{ width: '100%', maxHeight: '50vh', objectFit: 'contain', background: '#000' }}
      />
    </Box>
  </Stack>
);

/** Pre-migration events stored only the attributed camera's clip. */
const LegacyClip: FC<{ eventId: string }> = ({ eventId }) => {
  const { data, isLoading, isError } = useActivityEventVideoUrl(eventId, {
    enabled: true,
    staleTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false,
  });
  return (
    <Stack spacing={1}>
      <Typography variant={'caption'} color={'text.secondary'}>
        This check-in predates per-camera archiving — only the coded camera&apos;s clip was kept.
      </Typography>
      <Box sx={{ bgcolor: 'grey.900', display: 'grid', placeItems: 'center', minHeight: 240 }}>
        {isLoading && <CircularProgress />}
        {(isError || (!isLoading && !data)) && (
          <Typography variant={'body2'} color={'error.light'}>Clip unavailable.</Typography>
        )}
        {data && <video controls src={data.url} style={{ width: '100%', maxHeight: '60vh' }} />}
      </Box>
    </Stack>
  );
};

export default ActivityCaptureGallery;
